/**
 * Sentry error monitoring — initializes in production only.
 * Import this module at the top of main.tsx before React renders.
 */

import * as Sentry from '@sentry/react';
import { isFinanceDocument } from '../financePrivacy';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Skip Sentry initialization during react-snap prerender so puppeteer's
// synthetic errors don't pollute production telemetry.
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

if (
  SENTRY_DSN &&
  import.meta.env.PROD &&
  !IS_PRERENDERING &&
  !isFinanceDocument
) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: 'production',
    // Only send 20% of transactions to avoid quota issues
    tracesSampleRate: 0.2,
  });
}

/**
 * Capture an error in Sentry with optional context.
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (context) {
    Sentry.withScope(scope => {
      scope.setExtras(context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Set the current user for Sentry error reports.
 */
export function setUser(userId: string, email?: string): void {
  Sentry.setUser({ id: userId, email });
}
